// src/components/private/astapor/CouponCategory.js
// Component to manage transaction categories

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button, Form, Row, Col, Container, Modal, Alert, OverlayTrigger, Tooltip, Badge, ListGroup } from 'react-bootstrap';
import ConfirmationModal from '../common_components/ConfirmationModal';
import { toast } from 'react-toastify';
import FloatingActionButton from '../common_components/FloatingActionButton';
import '../../../custom.css';

const CouponCategory = () => {
    const [categories, setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [categoryFormData, setCategoryFormData] = useState({
        name: '',
        description: '',
        slug: ''
    });
    const [error, setError] = useState('');

    const token = localStorage.getItem('token');

    const [isEditing, setIsEditing] = useState(false);

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // Initialize toast notifications for success and error messages
    const notifySuccess = (message) => toast.success(message);
    const notifyError = (message) => toast.error(message);
    const notifyInfo = (message) => toast.info(message);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupon-categories/`, {
                headers: { Authorization: `Token ${token}` }
            });
            setCategories(response.data.results);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setError('Failed to fetch categories. Please try again.');
        }
    }, [token]);

    useEffect(() => {
        fetchCategories();
        document.title = 'Saarthi - Coupon Categories';
    }, [fetchCategories]);
    const handleModalClose = () => {
        setShowModal(false);
        setError('');  // Clear error message when modal is closed
        setCategoryFormData({ name: '', description: '', slug: '' });
        setIsEditing(false);
        setShowConfirmationModal(false);
    };

    const handleModalShow = (category = null) => {
        if (category) {
            console.log("Editing category:", category);
            setCurrentCategory(category);
            setCategoryFormData({ name: category.name, description: category.description, slug: category.slug });
            setIsEditing(true);
        } else {
            setCurrentCategory(null); // Clear current category when adding new
            setCategoryFormData({ name: '', description: '', slug: '' });
            setIsEditing(false);
        }
        setShowModal(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCategoryFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { name, description } = categoryFormData;
        if (!name.trim() || !description.trim()) {
            setError('Both name and description are required.');
            return;
        }

        if (isEditing && !currentCategory?.slug) {
            console.error('Slug is undefined:', currentCategory);
            setError('Unable to update category without valid identifier.');
            return;
        }

        try {
            if (isEditing) {
                console.log(`Updating category with slug: ${currentCategory.slug}`);
                await axios.patch(`${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupon-categories/${currentCategory.slug}/`, {
                    name, description
                }, {
                    headers: {
                        Authorization: `Token ${token}`
                    }
                });
                notifySuccess('Category updated successfully!');
            } else {
                await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupon-categories/`, {
                    name, description
                }, {
                    headers: {
                        Authorization: `Token ${token}`
                    }
                });
                notifySuccess('Category added successfully!');
            }
            fetchCategories();
            handleModalClose();

        } catch (error) {
            console.error('Error submitting category:', error);
            setError('Failed to submit category.');
            notifyError('Failed to add category. Please try again.');
        }
    };

    const promptDeleteCategory = (categorySlug) => {
        console.log("Received slug for deletion:", categorySlug);
        setShowConfirmationModal(true);
    };


    const confirmDeleteCategory = async () => {
        if (!currentCategory || !currentCategory.slug) {
            console.error('No category slug defined for deletion.');
            setError('No category selected for deletion.');
            return;
        }

        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupon-categories/${currentCategory.slug}/`, {
                headers: {
                    Authorization: `Token ${token}`
                }
            });
            console.log(`Category with slug ${currentCategory.slug} deleted successfully.`);
            fetchCategories();
            setShowModal(false); // Close the modal after deletion
            setShowConfirmationModal(false);
            notifyInfo('Category deleted successfully!');
        } catch (error) {
            console.error('Error deleting category:', error);
            setError('Failed to delete category.');
        }
    };


    return (
        <Container>
            <Row>
                <Col>
                    <h4 className="pt-0 pb-3">Coupon Categories</h4>
                </Col>
                {/* Original button for adding a new category */}
                {/*
                <Col className="d-flex justify-content-end">
                    <Button
                        style={{
                            width: '200px',
                            paddingLeft: '20px',
                            backgroundColor: '#4B88A2', // Custom green background
                            border: 'none',
                            color: 'white'
                        }}
                        onClick={() => handleModalShow()}>
                        < i class="bi bi-plus-circle-fill" style={{ marginRight: '8px' }}></i>
                        Add Category</Button>
                </Col>
                */}
            </Row>
            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Edit Category' : 'Add a new category'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit} noValidate>
                        {error && <Alert variant="danger">{error}</Alert>}
                        <Form.Group>
                            <Form.Label>Category Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter category name"
                                name="name"
                                value={categoryFormData.name}
                                onChange={handleChange}
                                isInvalid={!!error}
                            />
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter category description"
                                name="description"
                                value={categoryFormData.description}
                                onChange={handleChange}
                                isInvalid={!!error}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="primary" type="submit" className="me-2">
                                {isEditing ? 'Update' : 'Add Category'}
                            </Button>
                            {isEditing && currentCategory?.is_custom && (
                                <Button variant="danger" onClick={() => promptDeleteCategory(currentCategory.slug)}>
                                    Delete
                                </Button>
                            )}
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            <Row>
                <Col>
                    <ListGroup className="table-responsive">
                        {categories.map((category) => (
                            category.is_custom ? (
                                <OverlayTrigger
                                    key={category.slug}
                                    placement="top"
                                    overlay={<Tooltip id={`tooltip-${category.slug}`}>Click to open details</Tooltip>}
                                >
                                    <ListGroup.Item action onClick={() => handleModalShow(category)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span style={{ marginRight: '15px' }}>{category.name}</span>
                                            <div className="text-muted small">{category.description}</div>
                                        </div>
                                        <Badge bg="danger">CUSTOM</Badge>
                                    </ListGroup.Item>
                                </OverlayTrigger>
                            ) : (
                                <ListGroup.Item key={category.slug} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ marginRight: '15px' }}>{category.name}</span>
                                        <div className="text-muted small">{category.description}</div>
                                    </div>
                                </ListGroup.Item>
                            )
                        ))}
                    </ListGroup>
                </Col>
            </Row>
            <ConfirmationModal
                show={showConfirmationModal}
                onHide={() => setShowConfirmationModal(false)}
                onConfirm={confirmDeleteCategory}
                title="Confirm Deletion"
                body="Are you sure you want to delete this category? This action cannot be undone."
            />
            <FloatingActionButton
                actions={[
                    {
                        icon: 'bi bi-plus-circle-fill',
                        color: '#4B88A2',
                        handler: () => handleModalShow(),
                        tooltip: 'Add Category'
                    }
                ]}
            />
        </Container>
    );
};

export default CouponCategory;